/**
 * Replacing Google Meet with a Bundle meeting, for me (docs/features/calendar-meet-swap.md; rules in logic/meetSwap):
 * the one-time prompt on the page of an event I organise that has Meet ("Just this event" / "All my upcoming events" +
 * "Always use Bundle meetings…", "Not now"), the confirm (Google's "Send update emails to guests?": Cancel · Don't
 * email guests · Move and email guests), the toast with Undo during the run's 10-second hold, the run's progress (polled
 * every MEET_SWAP_POLL_MS while it goes), "Put the Google Meet links back", and Settings → Calendar's "Add a Bundle
 * meeting to new events I organise" (locked on under the workspace policy). Afterwards the event page's ⋯ menu offers
 * "Use a Bundle meeting" (`useForEvent`). Online only.
 */
import type { CalendarEvent, MeetSwapCandidatesResponse, MeetSwapRun } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { MEET_SWAP_POLL_MS, canRestore, canUseBundleMeeting, outsideGuests, runActive, scopeSkipped, showMeetPrompt, undoOpen } from "../logic/meetSwap.ts";
import { errorKind } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type MeetSwapScope = "event" | "upcoming";

/** The confirm: how many events and outside guests, and whether any of them come from Google Calendar. */
export type MeetSwapConfirm = { scope: MeetSwapScope; eventId?: string; count: number; guests: number; google: boolean; always: boolean; busy: boolean };

export type MeetSwapState = {
  status: "loading" | "ready" | "error";
  candidates?: MeetSwapCandidatesResponse;
  /** The prompt's choice (default: just this event, always on — as the design). */
  scope: MeetSwapScope;
  always: boolean;
  confirm?: MeetSwapConfirm;
  /** My latest run this session (swap or restore), refreshed while it goes. */
  run?: MeetSwapRun;
  /** The finished swap whose Google Meet links can be put back. */
  restorable?: MeetSwapRun;
  /** The toast: shown while the run is held (Undo cancels it). `notify`: guests are emailed. */
  toast?: { runId: string; count: number; notify: boolean };
  /** Events Google wouldn't let Bundle change (the scope banner). */
  scopeSkipped: number;
  /** Saving "Add a Bundle meeting to new events I organise". */
  saving: boolean;
  notice?: MessageDescriptor;
};

export class MeetSwapViewModel extends ViewModel<MeetSwapState> {
  private stopPoll: (() => void) | undefined;
  private loading: Promise<void> | undefined;

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", scope: "event", always: true, scopeSkipped: 0, saving: false });
    this.onDispose(() => this.stopPoll?.());
  }

  private get api() {
    return this.ws.deps.api;
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  /** Counts, the prompt's state, the policy and my active run (shared by the event page and Settings → Calendar). */
  load(): Promise<void> {
    this.loading ??= this.fetch().finally(() => {
      this.loading = undefined;
    });
    return this.loading;
  }

  private async fetch(): Promise<void> {
    try {
      const candidates = await this.api.meetSwapCandidates(this.ws.wsId);
      if (this.isDisposed) return;
      this.update({ status: "ready", candidates });
      if (candidates.activeRunId !== undefined && this.state.run?.id !== candidates.activeRunId) await this.follow(candidates.activeRunId);
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  // ---------- Event page ----------

  /** The one-time prompt shows on this event's page. */
  promptFor(e: CalendarEvent): boolean {
    return this.state.toast === undefined && showMeetPrompt(e, this.ws.state.meId, this.now(), this.state.candidates);
  }

  /** The ⋯ menu's "Use a Bundle meeting" applies to this event. */
  menuFor(e: CalendarEvent): boolean {
    const run = this.state.run;
    return (run === undefined || !runActive(run.status)) && canUseBundleMeeting(e, this.ws.state.meId, this.now(), this.state.candidates);
  }

  setScope(scope: MeetSwapScope): void {
    this.update({ scope });
  }

  setAlways(always: boolean): void {
    this.update({ always });
  }

  /** "Not now": the prompt is gone on every device (the ⋯ menu offers it afterwards). */
  async notNow(): Promise<void> {
    const c = this.state.candidates;
    if (c === undefined) return;
    this.update({ candidates: { ...c, prompt: "dismissed" } });
    try {
      await this.api.dismissMeetPrompt(this.ws.wsId);
    } catch (e) {
      if (!this.isDisposed) this.update({ candidates: c, notice: this.failure(e) });
    }
  }

  /** "Move to Bundle" (the prompt's choice) → the confirm. */
  moveToBundle(e: CalendarEvent): void {
    this.ask(this.state.scope, e, this.state.always);
  }

  /** The ⋯ menu's "Use a Bundle meeting": this event (its whole series) → the confirm. */
  useForEvent(e: CalendarEvent): void {
    this.ask("event", e, false);
  }

  private ask(scope: MeetSwapScope, e: CalendarEvent, always: boolean): void {
    const c = this.state.candidates;
    const confirm: MeetSwapConfirm =
      scope === "event" || c === undefined
        ? { scope: "event", eventId: e.id, count: 1, guests: outsideGuests(e), google: e.source === "google", always, busy: false }
        : { scope: "upcoming", count: Math.max(c.count, 1), guests: c.guests, google: c.googleScope > 0 || e.source === "google", always, busy: false };
    this.update({ confirm, notice: undefined });
  }

  dismissConfirm(): void {
    if (this.state.confirm?.busy !== true) this.update({ confirm: undefined });
  }

  /** "Don't email guests" (`notify` false) / "Move and email guests". Starts the run; the toast offers Undo. */
  async confirm(notify: boolean): Promise<void> {
    const k = this.state.confirm;
    if (k === undefined || k.busy) return;
    this.update({ confirm: { ...k, busy: true } });
    try {
      const runId = await this.api.startMeetSwap(this.ws.wsId, { scope: k.scope, ...(k.eventId === undefined ? {} : { eventId: k.eventId }), notify, ...(k.always ? { always: true } : {}) });
      if (this.isDisposed) return;
      const c = this.state.candidates;
      this.update({
        confirm: undefined,
        toast: { runId, count: k.count, notify },
        ...(c === undefined ? {} : { candidates: { ...c, prompt: "answered" as const, activeRunId: runId, ...(k.always ? { bundleMeetingDefault: true } : {}) } }),
      });
      await this.follow(runId);
    } catch (e) {
      if (!this.isDisposed) this.update({ confirm: undefined, notice: this.failure(e) });
    }
  }

  /** The toast's Undo (during the hold): nothing was sent. */
  async undo(): Promise<void> {
    const toast = this.state.toast;
    if (toast === undefined) return;
    this.update({ toast: undefined });
    try {
      const run = await this.api.cancelMeetSwap(this.ws.wsId, toast.runId);
      if (!this.isDisposed) this.adopt(run);
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: this.failure(e) });
    }
  }

  closeToast(): void {
    this.update({ toast: undefined });
  }

  // ---------- Settings → Calendar ----------

  /** "Add a Bundle meeting to new events I organise" is locked on by the workspace policy. */
  get locked(): boolean {
    return this.state.candidates?.policy === "bundle";
  }

  async setDefault(on: boolean): Promise<void> {
    const c = this.state.candidates;
    if (c === undefined || this.locked || this.state.saving) return;
    this.update({ saving: true, candidates: { ...c, bundleMeetingDefault: on } });
    try {
      const s = await this.api.updateCalendarSettings(this.ws.wsId, { bundleMeetingDefault: on });
      if (!this.isDisposed) this.update({ saving: false, candidates: { ...this.state.candidates!, bundleMeetingDefault: s.bundleMeetingDefault, policy: s.meetingPolicy, prompt: s.meetPrompt } });
    } catch (e) {
      if (!this.isDisposed) this.update({ saving: false, candidates: c, notice: this.failure(e) });
    }
  }

  /** "Put the Google Meet links back" for the finished swap. */
  async restore(): Promise<void> {
    const r = this.state.restorable;
    if (r === undefined || !canRestore(r, this.now())) return;
    this.update({ restorable: undefined });
    try {
      const runId = await this.api.restoreMeetSwap(this.ws.wsId, r.id);
      if (!this.isDisposed) await this.follow(runId);
    } catch (e) {
      if (!this.isDisposed) this.update({ restorable: r, notice: this.failure(e) });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }

  // ---------- The run ----------

  private async follow(runId: string): Promise<void> {
    try {
      const run = await this.api.meetSwapRun(this.ws.wsId, runId);
      if (!this.isDisposed) this.adopt(run);
    } catch {
      if (!this.isDisposed) this.poll(runId);
    }
  }

  private adopt(run: MeetSwapRun): void {
    const patch: Partial<MeetSwapState> = { run, scopeSkipped: run.kind === "swap" ? scopeSkipped(run) : this.state.scopeSkipped };
    const toast = this.state.toast;
    if (toast?.runId === run.id && !undoOpen(run, this.now()) && run.status !== "held") patch.toast = undefined;
    if (canRestore(run, this.now())) patch.restorable = run;
    else if (run.kind === "restore" && run.status !== "cancelled") patch.restorable = undefined;
    const active = runActive(run.status);
    const c = this.state.candidates;
    if (!active && c?.activeRunId === run.id) {
      const { activeRunId: _a, ...rest } = c;
      void _a;
      patch.candidates = rest;
    }
    this.update(patch);
    if (active) this.poll(run.id);
    else {
      this.stopPoll?.();
      this.stopPoll = undefined;
      // Counts changed (events moved or put back).
      if (run.status === "done") void this.load();
    }
  }

  private poll(runId: string): void {
    this.stopPoll?.();
    this.stopPoll = this.ws.deps.clock.setTimer(MEET_SWAP_POLL_MS, () => {
      this.stopPoll = undefined;
      if (!this.isDisposed) void this.follow(runId);
    });
  }

  private failure(e: unknown): MessageDescriptor {
    if (errorKind(e) === "network") return msg("settings.offline");
    if (errorKind(e) === "rateLimited") return msg("calendar.meetSwap.busy");
    return msg("calendar.meetSwap.failed");
  }
}
